import { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { computeLoyalty, LOYALTY_SILVER_MIN, LOYALTY_GOLD_MIN } from "@/lib/loyalty";

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [monthlySpend, setMonthlySpend] = useState(0);

  const refresh = useCallback(async () => {
    if (!user) { setItems([]); setMonthlySpend(0); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/cart");
      setItems(data);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const refreshLoyalty = useCallback(async () => {
    if (!user) { setMonthlySpend(0); return; }
    try {
      const { data } = await api.get("/loyalty/status");
      setMonthlySpend(data?.monthly_spend || 0);
    } catch {
      setMonthlySpend(0);
    }
  }, [user]);

  useEffect(() => { refresh(); refreshLoyalty(); }, [refresh, refreshLoyalty]);

  const updateItem = async (medicine_id, quantity) => {
    await api.post("/cart", { medicine_id, quantity });
    await refresh();
  };

  const addToCart = async (medicine, qty = 1) => {
    if (!user) {
      toast.error("Please sign in to add items to your cart");
      return false;
    }
    const existing = items.find((i) => i.medicine.id === medicine.id);
    const newQty = (existing?.quantity || 0) + qty;
    await updateItem(medicine.id, newQty);
    toast.success(`${medicine.name} added to cart`);
    return true;
  };

  const clear = async () => {
    await api.delete("/cart");
    await refresh();
  };

  const totalItems = items.reduce((s, i) => s + i.quantity, 0);
  const subtotal = items.reduce((s, i) => s + i.quantity * i.medicine.price, 0);
  const loyalty = computeLoyalty(subtotal, monthlySpend);

  // Celebratory toast the instant a loyalty threshold is crossed
  const prevSubtotalRef = useRef(null);
  useEffect(() => {
    const prev = prevSubtotalRef.current;
    if (prev !== null) {
      // Elite shoppers already get 20% flat — skip cart-tier toasts
      if (!(monthlySpend >= 10000)) {
        if (prev < LOYALTY_SILVER_MIN && subtotal >= LOYALTY_SILVER_MIN && subtotal < LOYALTY_GOLD_MIN) {
          const reward = Math.round(subtotal * 10) / 100;
          toast.success("You just unlocked 10% off!", {
            description: `Silver tier · saving ₹${reward.toFixed(2)} on this cart · add ₹${(LOYALTY_GOLD_MIN - subtotal).toFixed(0)} more for 15% off`,
            duration: 5000,
            className: "loyalty-toast",
          });
        } else if (prev < LOYALTY_GOLD_MIN && subtotal >= LOYALTY_GOLD_MIN) {
          const reward = Math.round(subtotal * 15) / 100;
          toast.success("You just unlocked 15% off!", {
            description: `Gold tier · saving ₹${reward.toFixed(2)} on this cart`,
            duration: 5500,
            className: "loyalty-toast loyalty-toast-gold",
          });
        }
      }
    }
    prevSubtotalRef.current = subtotal;
  }, [subtotal, monthlySpend]);

  const value = useMemo(
    () => ({ items, loading, refresh, refreshLoyalty, updateItem, addToCart, clear, totalItems, subtotal, monthlySpend, loyalty }),
    [items, loading, refresh, refreshLoyalty, totalItems, subtotal, monthlySpend, loyalty]
  );

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
